import { Backdrop, Fade, Grid, Modal } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { RiCloseLine } from 'react-icons/ri';
import { useAuth } from '../../../../../../hooks/auth';
import { useToast } from '../../../../../../hooks/toast';
import api from '../../../../../../services/api';
import {
  DeleteTamanhoButton,
  FlavorButton,
  HeaderColumn,
  NewTamanhoButton,
  TamanhoInput,
  TamanhoItem,
} from '../../../../../Produtos/components/Drawers/drawer_styles';
import { StatusSaleButton } from '../../../../../Produtos/components/styles';
import {
  ButtonStyled,
  InformativeSpan,
  ModalWrapper,
  PageTitle,
  Wrapper,
} from '../../styles';
import styleMaterial from '../../styles/styleMaterial';

const Sizes: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const classes = styleMaterial();

  const [tamanhos, setTamanhos] = useState<any[]>([]);
  const [selectedTamanho, setSelectedTamanho] = useState<any>();
  const [modalIsOpened, setModalIsOpened] = useState(false);
  const [confirmationDeleteText, setConfirmationDeleteText] = useState('');

  const handleSetTamanhoValue = useCallback(
    (id: number, field: string, value: any) => {
      setTamanhos((oldState) =>
        oldState.map((tamanho) => {
          if (String(tamanho.id) === String(id)) {
            return {
              ...tamanho,
              [field]: value,
            };
          }

          return tamanho;
        }),
      );
    },
    [],
  );

  const handleAddNewTamanho = useCallback(async () => {
    addToast({
      title: 'Seu tamanho está sendo criado.',
      type: 'info',
    });

    try {
      const response = await api.post('/tamanho', {
        descricao: 'Grande',
        ordem: tamanhos[tamanhos.length - 1].ordem,
        qtd_pedacos: 8,
        ativo: true,
        tipo: 3,
        qtd_sabores: 3,
        empresa_id: user.empresa_id,
      });

      setTamanhos((oldState) => [
        ...oldState,
        {
          id: response.data.id,
          descricao: response.data.descricao,
          numberOfFlavors: response.data.qtd_sabores,
          numberOfSlices: response.data.qtd_pedacos,
        },
      ]);

      addToast({
        title: 'Tamanho criado com sucesso.',
        type: 'success',
      });
    } catch (err) {
      addToast({
        title: 'Algo deu errado.',
        type: 'error',
        description:
          (err as any).response?.data?.message || 'Erro ao criar o tamanho',
      });
    }
  }, [user.empresa_id, tamanhos, addToast]);

  const handleSubmit = useCallback(async () => {
    const tamahosAreCorrect = tamanhos.some((tamanho: any) => tamanho.ativo);

    if (!tamahosAreCorrect) {
      addToast({
        type: 'error',
        title: 'Algo deu errado.',
        description: 'Pelo menos um tamanho deve estar ativo',
      });

      return;
    }

    try {
      addToast({
        type: 'info',
        title: 'Aguarde!',
        description: 'Os tamanhos estão sendo editados',
      });

      await Promise.all(
        tamanhos.map(async (tamanho: any) => {
          await api.put(`/tamanho/${tamanho.id}`, {
            descricao: tamanho.descricao,
            qtd_pedacos: tamanho.numberOfSlices,
            ativo: tamanho.ativo,
            tipo: 3,
            qtd_sabores: tamanho.numberOfFlavors,
          });
        }),
      );

      addToast({
        type: 'success',
        title: 'Sucesso!',
      });
    } catch (err) {
      const { message } = (err as any).response.data;

      addToast({
        type: 'error',
        title: 'Algo deu errado.',
        description: message,
      });
    }
  }, [tamanhos, addToast]);

  const handleDeleteTamanho = useCallback(
    async (id: string | number) => {
      setConfirmationDeleteText('');
      setSelectedTamanho(undefined);

      if (tamanhos.length > 1) {
        setTamanhos((oldState) =>
          oldState.filter((tamanho) => tamanho.id !== id),
        );

        await api.delete(`/tamanho/${id}`);
      } else {
        addToast({
          title: 'Ocorreu um erro!',
          type: 'error',
          description: 'É obrigatório ter pelo menos um tamanho!',
        });
      }
    },
    [tamanhos, addToast],
  );

  useEffect(() => {
    async function loadTamanhos(): Promise<void> {
      try {
        const response = await api.get('/tamanho');

        const parsedTamanhos = response.data.map((tamanho: any) => ({
          id: tamanho.id,
          descricao: tamanho.descricao,
          numberOfFlavors: tamanho.qtd_sabores,
          numberOfSlices: tamanho.qtd_pedacos,
          ordem: tamanho.ordem,
          ativo: tamanho.ativo,
        }));

        if (parsedTamanhos.length > 0) {
          setTamanhos(parsedTamanhos);

          return;
        }

        addToast({
          title: 'Aguarde',
          type: 'info',
          description: 'Seus novos tamanhos estão sendo criados',
        });

        const initialTamanhos = [
          {
            id: 1,
            descricao: 'Pequena',
            numberOfFlavors: 1,
            numberOfSlices: 1,
          },
          {
            id: 2,
            descricao: 'Média',
            numberOfFlavors: 2,
            numberOfSlices: 6,
          },
          {
            id: 3,
            descricao: 'Grande',
            numberOfFlavors: 3,
            numberOfSlices: 8,
          },
        ];

        const newTamanhos: any[] = [];

        await Promise.all(
          initialTamanhos.map(async (tamanho) => {
            const newTamanho = await api.post('/tamanho', {
              descricao: tamanho.descricao,
              ordem: tamanho.id,
              qtd_pedacos: tamanho.numberOfSlices,
              ativo: true,
              tipo: 3,
              qtd_sabores: tamanho.numberOfFlavors,
              empresa_id: user.empresa_id,
            });

            newTamanhos.push({
              id: newTamanho.data.id,
              descricao: newTamanho.data.descricao,
              numberOfFlavors: newTamanho.data.qtd_sabores,
              numberOfSlices: newTamanho.data.qtd_pedacos,
              ordem: newTamanho.data.ordem,
              ativo: newTamanho.data.ativo,
            });
          }),
        );

        setTamanhos(newTamanhos);
      } catch (err) {
        addToast({
          title: 'Algo deu errado',
          type: 'error',
          description:
            (err as any).response?.data?.message || 'Tente novamente.',
        });
      }
    }

    loadTamanhos();
  }, [user.empresa_id, addToast]);

  return (
    <Wrapper style={{ paddingLeft: 50, paddingRight: 50 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PageTitle>Tamanhos</PageTitle>
          <InformativeSpan>
            Indique aqui quais os tamanhos que suas pizzas são produzidas, em
            quantos pedaços são cortadas e até quantos sabores seu restaurante
            monta cada tamanho
          </InformativeSpan>
          <Grid container>
            <HeaderColumn>
              <p>Nome</p>
              <p>Qtd. Pedaços</p>
              <p>Qtd. Sabores</p>
            </HeaderColumn>
            <Grid container>
              {tamanhos
                .sort((a: any, b: any) => a.ordem - b.ordem)
                .map((tamanho) => (
                  <TamanhoItem className="parametro__tamanho" key={tamanho.id}>
                    <div className="container__tamanho__input">
                      <div>
                        <p>Nome</p>
                        <TamanhoInput
                          type="text"
                          defaultValue={tamanho.descricao}
                          placeholder="Nome"
                          onChange={(event) =>
                            handleSetTamanhoValue(
                              tamanho.id,
                              'descricao',
                              event.target.value,
                            )
                          }
                        />
                      </div>
                      <div>
                        <p>Qtd. Pedaços</p>
                        <TamanhoInput
                          type="number"
                          defaultValue={tamanho.numberOfSlices}
                          placeholder="Qtd. Pedaços"
                          onChange={(event) =>
                            handleSetTamanhoValue(
                              tamanho.id,
                              'numberOfSlices',
                              Number(event.target.value),
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="container__tamanho__button">
                      <div>
                        <p>Qtd. Sabores</p>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-around',
                          }}
                        >
                          <FlavorButton
                            isActive={tamanho.numberOfFlavors >= 1}
                            onClick={() =>
                              handleSetTamanhoValue(
                                tamanho.id,
                                'numberOfFlavors',
                                1,
                              )
                            }
                          >
                            <span>1</span>
                          </FlavorButton>
                          <FlavorButton
                            isActive={tamanho.numberOfFlavors >= 2}
                            onClick={() =>
                              handleSetTamanhoValue(
                                tamanho.id,
                                'numberOfFlavors',
                                2,
                              )
                            }
                          >
                            <span>2</span>
                          </FlavorButton>
                          <FlavorButton
                            isActive={tamanho.numberOfFlavors >= 3}
                            onClick={() =>
                              handleSetTamanhoValue(
                                tamanho.id,
                                'numberOfFlavors',
                                3,
                              )
                            }
                          >
                            <span>3</span>
                          </FlavorButton>
                          <FlavorButton
                            isActive={tamanho.numberOfFlavors >= 4}
                            onClick={() =>
                              handleSetTamanhoValue(
                                tamanho.id,
                                'numberOfFlavors',
                                4,
                              )
                            }
                          >
                            <span>4</span>
                          </FlavorButton>
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div
                          style={{
                            margin: 0,
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-end',
                            height: 'inherit',
                          }}
                        >
                          <div
                            onClick={(event) => event.stopPropagation()}
                            onFocus={(event) => event.stopPropagation()}
                          >
                            <StatusSaleButton
                              type="button"
                              isPaused={tamanho.ativo}
                              className="paused"
                              onClick={() =>
                                handleSetTamanhoValue(
                                  tamanho.id,
                                  'ativo',
                                  false,
                                )
                              }
                            >
                              Inativo
                            </StatusSaleButton>

                            <StatusSaleButton
                              type="button"
                              isPaused={!tamanho.ativo}
                              className="activated"
                              onClick={() =>
                                handleSetTamanhoValue(tamanho.id, 'ativo', true)
                              }
                            >
                              Ativo
                            </StatusSaleButton>
                          </div>
                        </div>
                        <Grid item xs={1}>
                          <DeleteTamanhoButton
                            onClick={() => setSelectedTamanho(tamanho)}
                          >
                            <FiTrash2 size={18} />
                          </DeleteTamanhoButton>
                        </Grid>
                      </div>
                    </div>
                  </TamanhoItem>
                ))}
            </Grid>
            <NewTamanhoButton onClick={handleAddNewTamanho}>
              <FiPlus size={15} />
              Novo tamanho
            </NewTamanhoButton>
            <Grid item xs={12}>
              <Grid container justify="center">
                <Grid item lg={4} md={6} xs={12}>
                  <ButtonStyled type="button" onClick={handleSubmit}>
                    Salvar
                  </ButtonStyled>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Modal
        className={classes.modal}
        open={selectedTamanho}
        onClose={() => setModalIsOpened(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={selectedTamanho}>
          <ModalWrapper>
            <div className="header">
              <h1>Atenção</h1>

              <button
                type="button"
                onClick={() => setSelectedTamanho(undefined)}
              >
                <RiCloseLine size={30} />
              </button>
            </div>

            <div className="description">
              <p>
                Ao confirmar, todos os produtos que estão relacionados a este
                tamanho também serão excluídos.
              </p>
              <br />
              <span>
                Digite &quot;excluir&quot; para remover o tamanho{' '}
                {selectedTamanho?.descricao}?
              </span>

              <input
                type="text"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  marginTop: 12,
                }}
                value={confirmationDeleteText}
                onChange={(e) => setConfirmationDeleteText(e.target.value)}
              />
            </div>

            <div className="btnActions">
              <button
                type="button"
                className="confirm"
                disabled={
                  confirmationDeleteText.toLocaleLowerCase() !== 'excluir'
                }
                onClick={() => handleDeleteTamanho(selectedTamanho?.id)}
              >
                Confirmar
              </button>

              <button
                type="button"
                className="cancel"
                onClick={() => setSelectedTamanho(undefined)}
              >
                Cancelar
              </button>
            </div>
          </ModalWrapper>
        </Fade>
      </Modal>
    </Wrapper>
  );
};

export default Sizes;
